import { useState, useMemo } from "react";
import { CalendarBlank, ListDashes } from "@phosphor-icons/react";
import { ScrollArea } from "../ui/scroll-area";
import { DealsFilterBar } from "./DealsFilterBar";
import { DealRow } from "./DealRow";
import { getCardClass, getTextPrimary, getTextSecondary, getDividerClass } from "../../lib/styleHelpers";

export function DealsLogTab({ dealsData, onViewOpportunity, isDark }) {
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [search, setSearch] = useState("");

  const deals = dealsData?.deals || [];

  const filtered = useMemo(() => {
    let result = deals;
    if (statusFilter !== "all") {
      result = result.filter((d) => d.pipeline_status === statusFilter);
    }
    if (categoryFilter !== "all") {
      result = result.filter((d) => d.category === categoryFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (d) =>
          d.title.toLowerCase().includes(q) ||
          d.category?.toLowerCase().includes(q)
      );
    }
    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- d/q/result are local loop vars, not deps
  }, [deals, statusFilter, categoryFilter, search]);

  const statusCounts = useMemo(() => {
    const counts = { all: deals.length, watching: 0, executed: 0, passed: 0, not_acted: 0 };
    for (const d of deals) {
      if (counts[d.pipeline_status] !== undefined) counts[d.pipeline_status]++;
    }
    return counts;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- counts/d are local vars, not deps
  }, [deals]);

  const categories = useMemo(
    () => [...new Set(deals.map((d) => d.category).filter(Boolean))],
    [deals]
  );

  return (
    <div className="animate-fade-in space-y-6" data-testid="deals-log-content">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className={`text-lg font-bold flex items-center gap-2 ${getTextPrimary(isDark)}`}>
            <CalendarBlank size={22} className="text-purple-500" />
            Deals Log
          </h2>
          <p className={`text-sm font-body ${getTextSecondary(isDark)}`}>
            {filtered.length} of {deals.length} deals
          </p>
        </div>
      </div>

      <DealsFilterBar
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        categoryFilter={categoryFilter}
        setCategoryFilter={setCategoryFilter}
        search={search}
        setSearch={setSearch}
        categories={categories}
        statusCounts={statusCounts}
        isDark={isDark}
      />

      {/* Deals List */}
      <div className={`rounded-xl border ${getCardClass(isDark)}`}>
        <div className={`p-4 border-b ${isDark ? "border-white/10" : "border-gray-200"}`}>
          <h3 className={`font-bold text-sm flex items-center gap-2 ${getTextPrimary(isDark)}`}>
            <ListDashes size={16} className="text-purple-500" />
            Deals Archive
          </h3>
        </div>

        {filtered.length === 0 ? (
          <div className="p-12 text-center">
            <CalendarBlank size={48} className={`mx-auto mb-4 ${isDark ? "text-gray-600" : "text-gray-300"}`} />
            <h3 className={`text-lg font-bold mb-2 ${getTextPrimary(isDark)}`}>No deals found</h3>
            <p className={`text-sm font-body ${getTextSecondary(isDark)}`}>
              Adjust your filters or search terms
            </p>
          </div>
        ) : (
          <ScrollArea className="max-h-[500px]">
            <div className={getDividerClass(isDark)} style={{ display: "flex", flexDirection: "column" }}>
              {filtered.map((deal) => (
                <DealRow
                  key={deal.id}
                  deal={deal}
                  onViewOpportunity={onViewOpportunity}
                  isDark={isDark}
                />
              ))}
            </div>
          </ScrollArea>
        )}
      </div>
    </div>
  );
}
